/**
 * IPv4 アドレス計算ユーティリティ。
 *
 * ここは純粋関数のみ。World を参照しない。
 * 「同じサブネットにいるか」「ネットワークアドレスは何か」といった判定は
 * すべてここに集約し、診断コマンド側では計算方法を持たない。
 */

/** APIPA（リンクローカル）アドレスのネットワーク */
export const APIPA_NETWORK = '169.254.0.0';
export const APIPA_MASK = '255.255.0.0';

/** ドット区切り表記が IPv4 として妥当か */
export function isValidIpv4(ip: string): boolean {
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  return parts.every((p) => {
    if (!/^\d{1,3}$/.test(p)) return false;
    const n = Number(p);
    return n >= 0 && n <= 255;
  });
}

/** '192.168.10.5' -> 3232238085（符号なし 32bit） */
export function ipToInt(ip: string): number {
  if (!isValidIpv4(ip)) {
    throw new Error(`不正な IPv4 アドレス: ${ip}`);
  }
  const [a, b, c, d] = ip.split('.').map(Number);
  return ((a << 24) >>> 0) + (b << 16) + (c << 8) + d;
}

/** 3232238085 -> '192.168.10.5' */
export function intToIp(value: number): string {
  const v = value >>> 0;
  return [(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff].join('.');
}

/**
 * サブネットマスクが「左詰めの連続する 1」になっているか。
 * 255.255.0.255 のような穴あきマスクは不正とする。
 */
export function isValidMask(mask: string): boolean {
  if (!isValidIpv4(mask)) return false;
  const v = ipToInt(mask);
  // ~v + 1 が 2 のべき乗（もしくは v が 0xffffffff）なら連続ビット
  const inverted = (~v >>> 0) + 1;
  return (inverted & (inverted - 1)) === 0;
}

/** '255.255.255.0' -> 24 */
export function maskToPrefix(mask: string): number {
  const v = ipToInt(mask);
  let count = 0;
  for (let i = 31; i >= 0; i--) {
    if ((v >>> i) & 1) count++;
    else break;
  }
  return count;
}

/** 24 -> '255.255.255.0' */
export function prefixToMask(prefix: number): string {
  if (prefix < 0 || prefix > 32) {
    throw new Error(`不正なプレフィックス長: ${prefix}`);
  }
  const v = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  return intToIp(v);
}

/** IP とマスクからネットワークアドレスを出す */
export function networkAddress(ip: string, mask: string): string {
  return intToIp((ipToInt(ip) & ipToInt(mask)) >>> 0);
}

/** IP とマスクからブロードキャストアドレスを出す */
export function broadcastAddress(ip: string, mask: string): string {
  const net = ipToInt(ip) & ipToInt(mask);
  const host = ~ipToInt(mask) >>> 0;
  return intToIp((net | host) >>> 0);
}

/** 2 つの IP が同一サブネットに属するか（マスクは両者に同じものを使う） */
export function sameSubnet(ipA: string, ipB: string, mask: string): boolean {
  if (!isValidIpv4(ipA) || !isValidIpv4(ipB) || !isValidIpv4(mask)) return false;
  return networkAddress(ipA, mask) === networkAddress(ipB, mask);
}

/** 使用可能なホストアドレス数（/31 と /32 は 0 として扱う） */
export function usableHostCount(mask: string): number {
  const prefix = maskToPrefix(mask);
  if (prefix >= 31) return 0;
  return 2 ** (32 - prefix) - 2;
}

/** APIPA（169.254.0.0/16）のアドレスか */
export function isApipa(ip: string | null): boolean {
  if (!ip || !isValidIpv4(ip)) return false;
  return sameSubnet(ip, APIPA_NETWORK, APIPA_MASK);
}

/**
 * ファイアウォールルール等で使う「サブネット表記」に IP が含まれるか。
 * 受け付ける表記:
 *   '*'                  … 任意
 *   '192.168.10.0/24'    … CIDR
 *   '192.168.10.0/255.255.255.0' … マスク表記
 *   '192.168.10.5'       … 単一ホスト
 */
export function ipInSubnetSpec(ip: string | null, spec: string): boolean {
  if (spec === '*' || spec === 'any') return true;
  if (!ip || !isValidIpv4(ip)) return false;

  const slash = spec.indexOf('/');
  if (slash < 0) {
    return isValidIpv4(spec) && spec === ip;
  }
  const base = spec.slice(0, slash);
  const suffix = spec.slice(slash + 1);
  if (!isValidIpv4(base)) return false;

  const mask = /^\d+$/.test(suffix) ? prefixToMask(Number(suffix)) : suffix;
  if (!isValidIpv4(mask)) return false;
  return sameSubnet(ip, base, mask);
}

/** '192.168.10.5' + '255.255.255.0' -> '192.168.10.0/24' */
export function toCidr(ip: string, mask: string): string {
  return `${networkAddress(ip, mask)}/${maskToPrefix(mask)}`;
}
